<template>
  <v-card class="mb-4 pa-2 card bg-background" color="background" variant="elevated" elevation="4" density="compact">
    <!-- Card Title Section -->
    <div class="d-flex align-center px-2 py-1">
      <v-tooltip :text="isConnected ? 'Connected' : 'Disconnected'" location="top">
        <template v-slot:activator="{ props }">
          <v-icon v-bind="props" :color="isConnected ? 'success' : 'error'" class="mr-2">
            {{ isConnected ? 'mdi-checkbox-marked-circle-outline' : 'mdi-alert-circle-outline' }}
          </v-icon>
        </template>
      </v-tooltip>

      <!-- Server Name Display/Edit -->
      <div class="d-flex align-center flex-grow-1">
        <v-card-title v-if="!editingName" class="max-85 pr-0 py-1">{{ name }}</v-card-title>
        <span v-else class="title-edit-wrapper max-85 pr-0" contenteditable="true" ref="titleEdit"
          @blur="saveNameFromEdit" @keydown.enter.prevent="saveNameFromEdit">
        </span>
        <Button v-if="!editingName && show && isAdmin" color="primary" class="ml-2" :click="toggleEditName" icon="mdi-pencil"
          size="small" tooltip="Edit Server Name" density="compact" />
        <Button v-if="editingName && show && isAdmin" color="primary" class="ml-2" :click="saveNameFromEdit" icon="mdi-check"
          size="small" tooltip="Save Name" density="compact" />
      </div>

      <v-btn color="primary" class="ml-auto" :icon="show ? 'mdi-chevron-up' : 'mdi-chevron-down'" @click="toggleExpand"
        density="compact"></v-btn>
    </div>
    <div v-if="show" class="mx-0 my-2 pa-0">
      <v-divider></v-divider>
    </div>
    <v-form class="ma-0 pa-0 bg-background" @submit.prevent density="compact">
      <v-expand-transition>
        <v-list class="ma-0 px-0 pt-0 pb-2 bg-background card-content" v-show="show" density="compact">

          <v-list-item :class="showGlobalTooltips ? 'mt-2 mb-n4 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact">
            <v-tooltip v-if="showGlobalTooltips" location="top" max-width="300"
              text="The command used to run this MCP server.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small" color="warning"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-text-field v-model="commandString" @blur="trackFieldUpdate('command_string')" label="Run Command"
              class="pt-2 text-overflow-ellipsis" variant="outlined" density="compact" clearable
              :rules="[() => !!commandString || 'Run Command is required']">
            </v-text-field>
          </v-list-item>

          <!-- NPM Package Link (if detected) -->
          <v-list-item v-if="npmPackage" :class="showGlobalTooltips ? 'mb-0 mt-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'"
            density="compact">
            <v-tooltip v-if="showGlobalTooltips" location="top" max-width="300"
              text="NPM package detected in the command.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small" color="warning"
                  class="global-tooltip-icon mt-2 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <div class="d-flex align-center">
              <v-icon class="mr-2" size="x-large">mdi-npm</v-icon>
              <a :href="npmPackageUrl" @click="viewNpmPackage" target="_blank" class="text-decoration-none npm-link"
                title="Go to NPM Package">{{ npmPackage }}</a>
            </div>
          </v-list-item>

          <v-list-item :class="showGlobalTooltips ? 'mt-0 mb-n4 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact">
            <v-tooltip v-if="showGlobalTooltips" location="top" max-width="300"
              text="A brief description of the MCP server.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small" color="warning"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-text-field v-model="description" :loading="mcpLoading" :disabled="mcpLoading" class="pt-2"
              @blur="trackFieldUpdate('description')" label="Server Description" variant="outlined" density="compact">
            </v-text-field>
          </v-list-item>

          <!-- Secrets Section (Multiple) -->
          <v-list-item :class="showGlobalTooltips ? 'mt-0 mb-n2 ml-6 mr-0 pa-0' : 'ma-0 pa-0'" density="compact">
            <v-tooltip v-if="showGlobalTooltips" location="top" max-width="300"
              text="Configure up to 5 secrets for this MCP server (e.g., API keys, tokens, passwords).">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small" color="warning"
                  class="global-tooltip-icon mt-3 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <div class="secrets-section">
              <div class="secrets-header d-flex align-center justify-space-between mb-3">
                <span class="text-subtitle-2">Server Secrets</span>
                <v-btn v-if="serverSecrets.length < 5" color="primary" variant="text" size="small"
                  prepend-icon="mdi-plus" @click="addSecret" density="compact">
                  Add Secret
                </v-btn>
              </div>

              <!-- Secret Fields -->
              <div v-for="(secret, index) in serverSecrets" :key="index"
                class="secret-row d-flex align-center gap-2 mb-1">
                <v-text-field v-model="secret.name" label="Secret Name (e.g., api_key)" placeholder="api_key"
                  variant="outlined" density="compact" class="secret-name-field" :rules="[
                    () => !!secret.name || 'Name is required',
                    () => (secret.name && secret.name.length <= 50) || 'Name must be 50 characters or less'
                  ]" />
                <v-text-field v-model="secret.value" :type="'password'" label="Secret Value"
                  placeholder="Enter secret value" variant="outlined" density="compact" class="secret-value-field" />
                <v-btn icon="mdi-delete" size="small" variant="text" color="error" @click="removeSecret(index)"
                  title="Remove this secret" />
              </div>

              <!-- Save All Secrets Button -->
              <v-btn v-if="serverSecrets.length > 0" color="primary" variant="outlined" size="small" block
                @click="saveAllSecrets" :loading="savingSecrets"
                :disabled="serverSecrets.every(s => !s.value.trim()) || serverSecrets.some(s => s.value.trim() !== '' && !s.name.trim())"
                class="mt-2 mb-3">
                Save All Secrets
              </v-btn>

              <!-- Empty State -->
              <div v-if="serverSecrets.length === 0" class="text-center text-body-2 text-medium-emphasis py-4">
                No secrets configured. Click "Add Secret" to add one.
              </div>
            </div>
          </v-list-item>

          <!-- Server Enabled Toggle -->
          <v-list-item v-if="isAdmin" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'"
            density="compact">
            <v-tooltip v-if="showGlobalTooltips" location="top" max-width="300"
              text="Enable or disable this MCP server.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small" color="warning"
                  class="global-tooltip-icon mt-3 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <div class="d-flex align-center">
              <span class="mr-2">Enabled</span>
              <v-switch v-model="enabled" color="primary" hide-details density="compact"
                @click="toggleServerEnabled(!enabled)"></v-switch>
            </div>
          </v-list-item>

          <!-- Server Logs -->
          <v-list-item v-if="isAdmin" :class="showGlobalTooltips ? 'my-0 ml-6 mr-0 pa-0' : 'ma-0 pa-0'"
            density="compact">
            <v-tooltip v-if="showGlobalTooltips" location="top" max-width="300" text="Server logs.">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small" color="warning"
                  class="global-tooltip-icon mt-5 ml-n6">mdi-information-outline</v-icon>
              </template>
            </v-tooltip>
            <v-expansion-panels>
              <v-expansion-panel title="Server Logs"
                @click="trackButtonClick('view_mcp_server_logs', 'click', { server_id: serverId })" density="compact"
                class="bg-background">
                <v-expansion-panel-text class="pa-0 ma-0 logs-panel">
                  <div v-if="logs && logs.length > 0" class="logs-container">
                    <pre class="logs-text">{{ logs.join('') }}</pre>
                  </div>
                  <p v-else class="text-body-2">No logs available</p>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-list-item>
        </v-list>
      </v-expand-transition>

      <v-card-actions density="compact" v-if="show && isAdmin">
        <!-- Action Buttons (only when expanded) -->
        <Button color="primary" class="mr-4" :disabled="saveDisabled" :click="updateServer" icon="mdi-check-bold"
          size="x-large" tooltip="Save Server" density="compact" />
        <Button color="primary" class="mr-4" :disabled="deleteDisabled" :click="deleteServer"
          icon="mdi-trash-can-outline" size="x-large" tooltip="Delete Server" density="compact" />
      </v-card-actions>
    </v-form>

    <div v-if="mcpLoading" class="card-action-overlay">
      <h2>{{ actionMessage }}</h2>
    </div>

    <!-- Error alert -->
    <v-alert v-if="mcpError" type="error" class="ma-2">
      {{ mcpError }}
    </v-alert>
  </v-card>
</template>

<script lang="ts">
import Button from '@/components/sub/Button.vue'
import { McpServer } from '@/store/mcp'
import { Options, mixins } from 'vue-class-component'
import AnalyticsMixin from '@/mixins/AnalyticsMixin'
import { mapGetters } from 'vuex'

@Options({
  props: {
    serverId: String
  },
  components: {
    Button
  },
  computed: {
    ...mapGetters(['mcpServers', 'mcpError', 'mcpLoading', 'showGlobalTooltips', 'isAdmin'])
  },
  watch: {
    // No watchers needed for enabled property
  }
})
export default class McpServerCard extends mixins(AnalyticsMixin) {
  private show_: boolean = true
  public serverId!: string
  public mcpServers!: Record<string, McpServer>
  public mcpError!: string | null
  public mcpLoading!: boolean
  public showGlobalTooltips!: boolean
  public isAdmin!: boolean
  
  public name: string = ''
  public commandString: string = ''
  public description: string = ''
  public authToken: string = ''  // Kept for backward compatibility, deprecated
  public statusMessage: string = 'Not connected'
  public enabled: boolean = false
  public logs: string[] = []
  public npmPackage: string = ''
  public npmPackageUrl: string = ''
  
  public showToken: boolean = false
  public waiting: boolean = false
  public actionMessage: string = ''
  public editingName: boolean = false
  public savingAuthToken: boolean = false  // Deprecated
  public secretName?: string = undefined  // Kept for backward compatibility, deprecated
  
  // New properties for multiple secrets
  public serverSecrets: Array<{name: string, value: string, hasExistingValue: boolean}> = []
  public savingSecrets: boolean = false
  
  public serverNameRules = [
    (v: string) => !!v || 'Server Name is required',
    (v: string) => (v && v.length <= 100) || 'Server Name must be less than 100 characters',
    (v: string) => (v && v.length >= 3) || 'Server Name must be at least 3 characters'
  ]
  
  created() {
    // Load server data if it exists
    if (this.serverId && this.mcpServers && this.mcpServers[this.serverId]) {
      const server = this.mcpServers[this.serverId]
      this.name = server.name || ''
      this.commandString = server.commandString || ''
      this.description = server.description || ''
      this.authToken = server.authToken || ''
      this.secretName = server.secretName  // Deprecated, kept for migration
      this.statusMessage = server.status || 'Not connected'
      this.enabled = server.enabled || false
      this.logs = server.logs || []
      this.npmPackage = server.npmPackage || ''
      this.npmPackageUrl = server.npmPackageUrl || ''
      
      // Load secrets - handle BOTH old and new format seamlessly
      if (server.secretNames && Array.isArray(server.secretNames) && server.secretNames.length > 0) {
        // New format - use as-is, show dots in value field
        this.serverSecrets = server.secretNames.map(name => ({
          name: name,
          value: '••••••••',  // Show dots as placeholder value
          hasExistingValue: true  // API sent these names, so values exist in backend
        }))
      } else if (server.secretName) {
        // Old format - auto-convert to new format in UI
        this.serverSecrets = [{
          name: server.secretName,
          value: '••••••••',  // Show dots as placeholder value
          hasExistingValue: true  // API sent this name, so value exists in backend
        }]
        console.log(`Auto-converted ${server.name}: secretName → secretNames[0]`)
      } else {
        // No secrets configured
        this.serverSecrets = []
      }
    }
    
    // If this is a new server, expand it automatically
    if (this.serverId && this.serverId.startsWith('new-server-')) {
      this.show = true
    } else {
      this.show = false
    }
  }
  
  get show(): boolean {
    return this.show_
  }
  
  set show(value: boolean) {
    this.show_ = value
  }
  
  get saveDisabled(): boolean {
    return !this.name || !this.commandString
  }
  
  get testDisabled(): boolean {
    return !this.commandString
  }
  
  get deleteDisabled(): boolean {
    return false
  }
  
  get isConnected(): boolean {
    return this.statusMessage === 'Connected successfully' || 
           (this.mcpServers[this.serverId]?.status === 'connected');
  }
  
  toggleExpand() {
    this.show = !this.show
    this.trackButtonClick('toggle_mcp_server_card', this.show ? 'expand' : 'collapse', { server_id: this.serverId });
    if (this.show) {
      this.$emit('server-selected', this.serverId)
    }
  }
  
  checkHash() {
    // This method can be used to check if changes have been made
  }
  
  toggleEditName() {
    this.trackButtonClick('edit_mcp_server_name_start', 'click', { server_id: this.serverId });
    this.editingName = !this.editingName
    
    if (this.editingName) {
      // Need to wait for the DOM to update before setting content and focus
      this.$nextTick(() => {
        const titleEdit = this.$refs.titleEdit as HTMLElement
        if (titleEdit) {
          titleEdit.innerText = this.name
          titleEdit.focus()
          // Place cursor at the end
          const range = document.createRange()
          const selection = window.getSelection()
          range.selectNodeContents(titleEdit)
          range.collapse(false)
          selection?.removeAllRanges()
          selection?.addRange(range)
        }
      })
    }
  }
  
  saveName() {
    this.editingName = false
    this.checkHash()
  }
  
  saveNameFromEdit() {
    this.trackButtonClick('edit_mcp_server_name_save', 'save', { server_id: this.serverId });
    const titleEdit = this.$refs.titleEdit as HTMLElement
    if (titleEdit) {
      const newName = titleEdit.innerText.trim()
      
      // Validate the name
      if (newName.length >= 3 && newName.length <= 100) {
        this.name = newName
      }
    }
    
    this.editingName = false
    this.checkHash()
  }
  
  async testConnection() {
    this.actionMessage = 'Testing connection...'
    
    try {
      const result = await this.$store.dispatch('testMcpServer', {
        id: this.serverId,
        commandString: this.commandString,
        authToken: this.authToken
      })
      
      if (result && result.success) {
        this.statusMessage = 'Connected successfully'
      } else {
        this.statusMessage = result.message || 'Connection failed'
      }
    } catch (error) {
      console.error('Error testing MCP server connection:', error)
      this.statusMessage = 'Connection failed'
    }
  }
  
  async updateServer() {
    this.trackButtonClick('update_mcp_server', 'save', { server_id: this.serverId });
    if (this.saveDisabled) return
    
    this.actionMessage = 'Saving...'
    
    const serverConfig: McpServer = {
      id: this.serverId,
      name: this.name,
      timestamp: Date.now(),
      commandString: this.commandString,
      description: this.description,
      authToken: this.authToken,
      enabled: this.enabled,
      logs: this.logs,
      npmPackage: this.npmPackage,
      npmPackageUrl: this.npmPackageUrl
    }
    
    try {
      await this.$store.dispatch('updateMcpServer', serverConfig)
    } catch (error) {
      console.error('Error updating MCP server:', error)
    }
  }
  
  async deleteServer() {
    this.trackButtonClick('delete_mcp_server', 'click', { server_id: this.serverId });
    this.actionMessage = 'Deleting...'
    
    try {
      await this.$store.dispatch('deleteMcpServer', this.serverId)
    } catch (error) {
      console.error('Error deleting MCP server:', error)
    }
  }
  
  async toggleServerEnabled(enabled: boolean) {
    this.trackButtonClick('toggle_mcp_server_enabled', 'click', { server_id: this.serverId, enabled });
    this.actionMessage = enabled ? 'Enabling server...' : 'Disabling server...'
    
    try {
      const success = await this.$store.dispatch(
        enabled ? 'enableMcpServer' : 'disableMcpServer', 
        this.serverId
      )
      
      if (!success) {
        // If the action failed, revert the switch to its previous state
        this.enabled = !enabled
      }
    } catch (error) {
      console.error(`Error ${enabled ? 'enabling' : 'disabling'} MCP server:`, error)
      // Revert the switch to its previous state
      this.enabled = !enabled
    }
  }

  trackFieldUpdate(fieldName: string) {
    this.trackFormInteraction('mcp_server_form', 'update_field', fieldName);
  }

  viewNpmPackage() {
    this.trackButtonClick('view_mcp_npm_package', 'click', { server_id: this.serverId, package_name: this.npmPackage });
  }

  /**
   * Add a new secret slot (max 5)
   */
  addSecret() {
    if (this.serverSecrets.length >= 5) {
      this.$store.dispatch('updateSnackbar', { 
        text: 'Maximum 5 secrets allowed per server' 
      })
      return
    }
    
    this.serverSecrets.push({
      name: '',
      value: '',
      hasExistingValue: false  // New secret, no value in backend yet
    })
    
    this.trackButtonClick('add_mcp_server_secret', 'click', { 
      server_id: this.serverId,
      secret_count: this.serverSecrets.length 
    })
  }

  /**
   * Remove a secret at the specified index
   */
  removeSecret(index: number) {
    if (index < 0 || index >= this.serverSecrets.length) {
      return
    }
    
    const secretName = this.serverSecrets[index].name
    this.serverSecrets.splice(index, 1)
    
    this.trackButtonClick('remove_mcp_server_secret', 'click', { 
      server_id: this.serverId,
      secret_name: secretName,
      remaining_count: this.serverSecrets.length 
    })
  }

  /**
   * Save all configured secrets for this server
   */
  async saveAllSecrets() {
    this.trackButtonClick('save_all_mcp_server_secrets', 'click', { 
      server_id: this.serverId,
      secret_count: this.serverSecrets.length 
    })
    
    // Filter out placeholder dots and empty values - only save real entered values
    const secretsToSave = this.serverSecrets.filter(s => {
      const val = s.value.trim()
      return val !== '' && val !== '••••••••'  // Exclude placeholder dots
    })
    
    // If no real values entered, inform user
    if (secretsToSave.length === 0) {
      this.$store.dispatch('updateSnackbar', { 
        text: 'No secrets to update. Enter values for secrets you want to change or add.' 
      })
      return
    }
    
    // Validate only the secrets being saved have names
    const invalidSecrets = secretsToSave.filter(s => !s.name.trim())
    if (invalidSecrets.length > 0) {
      this.$store.dispatch('updateSnackbar', { 
        text: 'All secrets must have a name' 
      })
      return
    }
    
    // Check for duplicate names in secrets being saved
    const names = secretsToSave.map(s => s.name.trim())
    const uniqueNames = new Set(names)
    if (names.length !== uniqueNames.size) {
      this.$store.dispatch('updateSnackbar', { 
        text: 'Secret names must be unique' 
      })
      return
    }
    
    this.savingSecrets = true
    
    try {
      // Send only secrets with real values (excludes dots placeholder and empty)
      const success = await this.$store.dispatch('saveServerSecrets', {
        serverName: this.name,
        secrets: secretsToSave.map(s => ({
          name: s.name.trim(),
          value: s.value.trim()
        }))
      })
      
      if (success) {
        // Get ALL secret names (including ones user didn't update)
        const allSecretNames = this.serverSecrets
          .map(s => s.name.trim())
          .filter(n => n !== '')
        
        const serverConfig: McpServer = {
          id: this.serverId,
          name: this.name,
          timestamp: Date.now(),
          commandString: this.commandString,
          description: this.description,
          enabled: this.enabled,
          logs: this.logs,
          npmPackage: this.npmPackage,
          npmPackageUrl: this.npmPackageUrl,
          secretNames: allSecretNames
        }
        
        await this.$store.dispatch('updateMcpServer', serverConfig)
        
        // Reset values to dots for existing secrets, empty for new ones
        this.serverSecrets.forEach(s => {
          if (s.name.trim() !== '') {
            s.value = '••••••••'  // Show dots for all saved secrets
            s.hasExistingValue = true
          }
        })
      }
    } catch (error) {
      console.error('Error saving secrets:', error)
      this.$store.dispatch('updateSnackbar', { 
        text: `Failed to save secrets: ${error}` 
      })
    } finally {
      this.savingSecrets = false
    }
  }
}
</script>

<style scoped lang="scss">
.card {
  border: 1px solid rgba(255, 255, 255, 0.3);
}
.card-action-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.max-85 {
  max-width: 85%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.global-tooltip-icon {
  color: rgba(255, 255, 255, 0.7);
}

.logs-panel {
  :deep(.v-expansion-panel-text__wrapper) {
    padding: 0 !important;
  }
}

.logs-container {
  max-height: 300px;
  overflow-y: auto;
  overflow-x: visible;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 8px;
}

.logs-text {
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 0;
  font-size: 11px;
  line-height: 1.3;
}

.npm-link {
  font-weight: bold;
}

.name-edit-field {
  min-width: 300px;
  max-width: 400px;
}

.title-edit-wrapper {
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: 0.0125em;
  padding: 0.5rem 0;
  min-width: 100px;
  outline: none;
  border-bottom: 2px solid rgba(var(--v-theme-primary), 0.7);
  display: inline-block;
  margin: 0;
  color: inherit;
}

.text-overflow-ellipsis {
  :deep(input) {
    text-overflow: ellipsis;
  }
}

.secrets-section {
  width: 100%;
}

.secret-row {
  gap: 8px;
}

.secret-name-field {
  flex: 1 1 40%;
  min-width: 150px;
}

.secret-value-field {
  flex: 1 1 60%;
  min-width: 200px;
}
</style>
